<template>
  <div>
    <b-row>
      <b-col align="right">
        <b-button
          variant="outline-primary"
          class="mb-1"
          :disabled="loading"
          @click="fetchItems"
        >
          <feather-icon
            icon="RefreshCwIcon"
            size="14"
            class="mr-50"
          />
          Refresh
        </b-button>
      </b-col>
    </b-row>

    <div
      v-if="loading"
      class="text-center"
    >
      <b-spinner
        label="Small Spinner"
        variant="primary"
      />
    </div>

    <b-alert
      variant="danger"
      :show="loadingError ? true : false"
    >
      <div class="alert-body">
        <p>
          {{ loadingError }}
        </p>
      </div>
    </b-alert>

    <b-table
      v-if="!loading && !loadingError"
      :items="results"
      :fields="tableFields"
      responsive
      striped
      hover
      show-empty
      empty-text="No payment orders found"
      :sort-by="sortBy"
      :sort-desc="sortDesc"
      class="compact-table payment-order-table"
      no-local-sorting
      @sort-changed="sortingChanged"
    >
      <template #head(reconciled_by)>
        Updated By<br>In Marg
      </template>
      <template #head(reconciled_at)>
        Updated in<br>Marg At
      </template>

      <template #cell(doc_number)="{ item }">
        <span class="text-primary font-weight-bold">
          {{ getDocNumber(item) }}
        </span>
      </template>
      <template #cell(target_type)="{ item }">
        {{ capitalize(item.target_type) }}
      </template>
      <template #cell(amount)="{ item }">
        {{ formateNumber(item.amount) }}
      </template>
      <template #cell(status)="{ item }">
        <app-chip :variant="getStatusChipVariant(item.status)">
          {{ capitalize(item.status) }}
        </app-chip>
      </template>
      <template #cell(paid_at)="{ item }">
        {{ item.paid_at ? formatedDate(item.paid_at) : '-' }}
      </template>
      <template #cell(marg_status)="{ item }">
        <app-chip
          v-if="item.allocations && item.allocations.length"
          :variant="isReconciled(item) ? 'success' : 'warning'"
        >
          {{ isReconciled(item) ? 'Updated' : 'Pending' }}
        </app-chip>
        <span v-else>-</span>
      </template>
      <template #cell(reconciled_by)="{ item }">
        {{ getReconciledBy(item) }}
      </template>
      <template #cell(reconciled_at)="{ item }">
        {{ getReconciledAt(item) ? formatedDate(getReconciledAt(item)) : '-' }}
      </template>
      <template #cell(action)="{ item }">
        <feather-icon
          v-b-tooltip.hover="{boundary:'window'}"
          title="View Details"
          icon="EyeIcon"
          size="20"
          class="cursor-pointer"
          @click="selectedDetailItem = item"
        />
      </template>
    </b-table>

    <detailed-pagination
      v-if="!loading && !loadingError"
      :total-items="totalItems"
      :per-page="perPage"
      :current-page="currentPage"
      @page-changed="(page) => {currentPage = page}"
    />

    <payment-order-details-modal
      v-if="selectedDetailItem"
      :item="selectedDetailItem"
      @modal-closed="selectedDetailItem = null"
    />
  </div>
</template>

<script>
import {
  BSpinner, BTable, BAlert, BRow, BCol, BButton, VBTooltip,
} from 'bootstrap-vue'
import axios from 'axios'

import dateMixin from '@/mixins/dateMixin'
import formatMixin from '@/mixins/formatMixin'
import getParsedQueryString from '@/utils/headerQueryParser'
import AppChip from '@/components/UI/AppChip.vue'
import DetailedPagination from '@/components/UI/DetailedPagination.vue'
import PaymentOrderDetailsModal from './PaymentOrderDetailsModal.vue'
// API only supports sorting on these fields
const SORTABLE_FIELDS = ['created_at', 'paid_at', 'amount']

export default {
  directives: {
    'b-tooltip': VBTooltip,
  },
  components: {
    BSpinner,
    BTable,
    BAlert,
    BRow,
    BCol,
    BButton,
    AppChip,
    DetailedPagination,
    PaymentOrderDetailsModal,
  },
  mixins: [dateMixin, formatMixin],
  props: {
    filters: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      loading: true,
      loadingError: null,

      results: [],
      tableFields: [
        { key: 'doc_number', label: 'Doc Number' },
        { key: 'order_id', label: 'Order ID' },
        { key: 'target_type', label: 'Target Type' },
        { key: 'buyer_identifier', label: 'Buyer' },
        { key: 'initiated_by_full_name', label: 'Initiated By' },
        {
          key: 'amount', label: 'Amount', sortable: true, class: 'text-right pr-2',
        },
        { key: 'status', label: 'Status' },
        { key: 'payment_method', label: 'Payment Method' },
        { key: 'paid_at', label: 'Paid At', sortable: true },
        { key: 'marg_status', label: 'Marg Status' },
        { key: 'reconciled_by', label: 'Updated By In Marg' },
        { key: 'reconciled_at', label: 'Updated in Marg At' },
        {
          key: 'action', label: 'Action', class: 'sticky-action text-center',
        },
      ],

      selectedDetailItem: null,

      currentPage: 1,
      perPage: 50,
      totalItems: 0,
      sortBy: 'created_at',
      sortDesc: true,
    }
  },
  watch: {
    currentPage() {
      this.fetchItems()
    },
    filters() {
      if (this.currentPage !== 1) {
        this.currentPage = 1
      } else {
        this.fetchItems()
      }
    },
  },
  created() {
    this.fetchItems()
  },
  methods: {
    sortingChanged(ctx) {
      if (!SORTABLE_FIELDS.includes(ctx.sortBy)) return
      this.sortBy = ctx.sortBy
      this.sortDesc = ctx.sortDesc
      if (this.currentPage !== 1) {
        this.currentPage = 1
      } else {
        this.fetchItems()
      }
    },

    fetchItems() {
      const params = {
        sort_by: `${this.sortDesc ? '-' : ''}${this.sortBy}`,
        page: this.currentPage,
        per_page: this.perPage,
        ...this.filters,
      }

      this.loading = true
      axios.get(`/payments/payment_orders?${getParsedQueryString(params)}`)
        .then(res => {
          this.results = res.data.results
          this.totalItems = res.data.count
          this.loadingError = null
          this.loading = false
        }).catch(error => {
          this.loadingError = error?.response?.data?.error
            || error?.response?.data?.detail
            || 'Something went wrong'
          this.loading = false
        })
    },

    getDocNumber(item) {
      if (item.target_type === 'statement') {
        return item.target_id || '-'
      }
      // documents, not allocations: allocations only exist for successful payments
      return item.documents?.[0]?.unique_doc_no || '-'
    },

    isReconciled(item) {
      return item.allocations.every(allocation => allocation.reconciled_in_marg)
    },

    getReconciledBy(item) {
      const names = (item.allocations || [])
        .map(allocation => allocation.reconciled_by_full_name)
        .filter(Boolean)
      return names.length ? [...new Set(names)].join(', ') : '-'
    },

    getReconciledAt(item) {
      const dates = (item.allocations || [])
        .map(allocation => allocation.reconciled_at)
        .filter(Boolean)
        .sort()
      return dates.length ? dates[dates.length - 1] : null
    },

    capitalize(value) {
      if (!value) return '-'
      return value.charAt(0).toUpperCase() + value.slice(1)
    },

    getStatusChipVariant(value) {
      if (value === 'success') return 'success'
      if (value === 'pending') return 'warning'
      if (value === 'failed') return 'danger'
      return 'secondary'
    },
  },
}
</script>

<style scoped>
.payment-order-table ::v-deep th,
.payment-order-table ::v-deep td {
  white-space: nowrap;
  vertical-align: middle;
}

/* Action column stays fixed on the right while the rest scrolls */
.payment-order-table ::v-deep .sticky-action {
  position: sticky;
  right: 0;
  z-index: 2;
  background-color: #fff;
  box-shadow: -2px 0 4px rgba(0, 0, 0, 0.08);
}
.payment-order-table ::v-deep thead .sticky-action {
  z-index: 3;
  background-color: #f3f2f7;
}

/* Dark theme */
.dark-layout .payment-order-table ::v-deep .sticky-action {
  background-color: #283046;
}
.dark-layout .payment-order-table ::v-deep thead .sticky-action {
  background-color: #343d55;
}
</style>
